import {createFeatureSelector, createSelector} from '@ngrx/store';
import {isPopulatedArray, isPopulatedString} from '@aviary/store/common/typing.helpers';
import {generateIndependentSelectorCreatorsFor, generateLoadStatusSelectorsFor, generateSelectorsDependentOn} from '@aviary/store/store.selector-generators';
import {MxSelector, MxSelectorCreator, NO_NAME_PREFIX} from '@aviary/store/store.types';
import {BALANCE_FEATURE_KEY, PatronBalanceLine, PatronBalanceTransaction, SquareAppDetails} from '@store/balance/balance.types';
import {FALLBACK_CREDIT_TYPE, FALLBACK_PATRON_BALANCE_LINE, FALLBACK_PATRON_BALANCE_TRANSACTION, FALLBACK_SQUARE_APP_DETAILS} from './balance.fallback';
import {BalanceState} from './balance.state';

export const entireState: MxSelector<BalanceState> = createFeatureSelector(BALANCE_FEATURE_KEY);

export namespace BalanceSelectors {
  export namespace BalanceTransactions {
    const substate: MxSelector<BalanceState['balanceTransactions']> = createSelector(
      entireState,
      ({balanceTransactions}) => balanceTransactions
    );

    export const transactions: MxSelector<PatronBalanceTransaction[]> = createSelector(
      substate,
      ({data}) => data
    );

    export const {
      loading,
      loaded,
      awaitingFirstLoad,
      awaitingAnyLoad
    } = generateLoadStatusSelectorsFor(substate);

    export const includeAllTransactions: MxSelector<boolean> = createSelector(
      substate,
      ({includeAllTransactions}) => includeAllTransactions
    );

    export const {
      createdFor,
      idFor,
      nameFor,
      itemTitleFor,
      amountFor,
      amountOutstandingFor,
      typeFor,
      balanceLinesFor,
      opacNoteFor,
    } = generateIndependentSelectorCreatorsFor(FALLBACK_PATRON_BALANCE_TRANSACTION, NO_NAME_PREFIX);

    export const hasBalanceReducingTransactionTypeFor: MxSelectorCreator<[PatronBalanceTransaction], boolean> =
      balanceTransaction => createSelector(
        typeFor(balanceTransaction),
        type => ['PAY_FROM_EXTERNAL', 'PAY_FROM_WALLET', 'WAIVE_CHARGE', 'ADD_CREDIT_TO_WALLET'].includes(type)
      );

    export const amountOutstandingIsNegativeFor: MxSelectorCreator<[PatronBalanceTransaction], boolean> =
      balanceTransaction => createSelector(
        amountOutstandingFor(balanceTransaction),
        amount => amount < 0
      );

    export const showAmountsExpansionFor: MxSelectorCreator<[PatronBalanceTransaction], boolean> =
      balanceTransaction => createSelector(
        balanceLinesFor(balanceTransaction),
        typeFor(balanceTransaction),
        (balanceLines, type) =>
          balanceLines.length > 1 && type === 'PAY_FROM_EXTERNAL'
      );

    export const showDetailsExpansionFor: MxSelectorCreator<[PatronBalanceTransaction], boolean> =
      balanceTransaction => createSelector(
        typeFor(balanceTransaction),
        opacNoteFor(balanceTransaction),
        (type, opacNote) =>
          type === 'ADD_CHARGE' && isPopulatedString(opacNote)
      );

    export const sortedBalanceLinesFor: MxSelectorCreator<[PatronBalanceTransaction], PatronBalanceLine[]> =
      balanceTransaction => createSelector(
        balanceLinesFor(balanceTransaction),
        (balanceLines) => [...balanceLines].sort((a, b) => a.target.localeCompare(b.target))
      );

    export const hasAnyTransactions: MxSelector<boolean> = createSelector(
      transactions,
      isPopulatedArray
    );

  } // namespace BalanceTransactions

  export namespace BalanceTransactionLines {
    export const {
      nameFor,
      targetFor,
      amountFor,
    } = generateIndependentSelectorCreatorsFor(FALLBACK_PATRON_BALANCE_LINE, NO_NAME_PREFIX);

  } // namespace BalanceTransactionLines

  export namespace SquareApp {
    const substate: MxSelector<BalanceState['squareAppDetails']> = createSelector(
      entireState,
      ({squareAppDetails}) => squareAppDetails
    );

    export const details: MxSelector<SquareAppDetails> = createSelector(
      substate,
      ({data}) => data
    );

    const {
      creditType
    } = generateSelectorsDependentOn(details, FALLBACK_SQUARE_APP_DETAILS, NO_NAME_PREFIX);

    export const {
      minimumAmount
    } = generateSelectorsDependentOn(creditType, FALLBACK_CREDIT_TYPE, NO_NAME_PREFIX);

    export const {
      loading,
      loaded,
      awaitingFirstLoad,
      awaitingAnyLoad
    } = generateLoadStatusSelectorsFor(substate);

    export const hasSquareConfig: MxSelector<boolean> = createSelector(
      details,
      ({appId, locationId}) => isPopulatedString(appId) && isPopulatedString(locationId)
    );

  } // namespace SquareApp

  export namespace DependentBalanceTransactions {
    const substate: MxSelector<BalanceState['dependentBalanceTransactions']> = createSelector(
      entireState,
      ({dependentBalanceTransactions}) => dependentBalanceTransactions
    );

    export const transactions: MxSelector<PatronBalanceTransaction[]> = createSelector(
      substate,
      ({data}) => data
    );

    export const {
      loading,
      loaded,
      awaitingFirstLoad,
      awaitingAnyLoad
    } = generateLoadStatusSelectorsFor(substate);

    export const includeAllTransactions: MxSelector<boolean> = createSelector(
      substate,
      ({includeAllTransactions}) => includeAllTransactions
    );

    export const {
      createdFor,
      idFor,
      nameFor,
      itemTitleFor,
      amountFor,
      amountOutstandingFor,
      typeFor,
      balanceLinesFor,
      opacNoteFor,
    } = generateIndependentSelectorCreatorsFor(FALLBACK_PATRON_BALANCE_TRANSACTION, NO_NAME_PREFIX);

    export const hasBalanceReducingTransactionTypeFor: MxSelectorCreator<[PatronBalanceTransaction], boolean> =
      balanceTransaction => createSelector(
        typeFor(balanceTransaction),
        type => ['PAY_FROM_EXTERNAL', 'PAY_FROM_WALLET', 'WAIVE_CHARGE', 'ADD_CREDIT_TO_WALLET'].includes(type)
      );

    export const amountOutstandingIsNegativeFor: MxSelectorCreator<[PatronBalanceTransaction], boolean> =
      balanceTransaction => createSelector(
        amountOutstandingFor(balanceTransaction),
        amount => amount < 0
      );

    export const showAmountsExpansionFor: MxSelectorCreator<[PatronBalanceTransaction], boolean> =
      balanceTransaction => createSelector(
        balanceLinesFor(balanceTransaction),
        typeFor(balanceTransaction),
        (balanceLines, type) =>
          balanceLines.length > 1 && type === 'PAY_FROM_EXTERNAL'
      );

    export const showDetailsExpansionFor: MxSelectorCreator<[PatronBalanceTransaction], boolean> =
      balanceTransaction => createSelector(
        typeFor(balanceTransaction),
        opacNoteFor(balanceTransaction),
        (type, opacNote) =>
          type === 'ADD_CHARGE' && isPopulatedString(opacNote)
      );

    export const sortedBalanceLinesFor: MxSelectorCreator<[PatronBalanceTransaction], PatronBalanceLine[]> =
      balanceTransaction => createSelector(
        balanceLinesFor(balanceTransaction),
        (balanceLines) => [...balanceLines].sort((a, b) => a.target.localeCompare(b.target))
      );

    export const hasAnyTransactions: MxSelector<boolean> = createSelector(
      transactions,
      isPopulatedArray
    );

  } // namespace BalanceTransactions
}
